import { XIcon } from '@heroicons/react/outline';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationIcon,
  InformationCircleIcon,
} from '@heroicons/react/solid';
import classNames from 'classnames';
import { useCallback } from 'react';
import { ToastContainer, ToastContainerProps } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { twColors } from '../../colors';
import { InlineLoader } from '../inline-loader';
import { CdsToastOptions, CdsToastType, CdsToastTypeColorMap } from './models';

export const CdsToastContainer = ({
  position = 'bottom-left',
  autoClose = 5000,
  closeButton = false,
  closeOnClick = false,
  theme = 'dark',
  className,
  ...props
}: ToastContainerProps) => {
  return (
    <ToastContainer
      {...props}
      position={position}
      autoClose={autoClose}
      closeButton={closeButton}
      closeOnClick={closeOnClick}
      theme={theme}
      className={classNames('ms-32', className)}
    />
  );
};

export const CdsToastBody = ({
  message,
  type,
  onClose,
  buttonText,
  buttonOnClick,
  showLoader = false,
  closeButton = true,
}: CdsToastOptions) => {
  const getToastIconByType = useCallback((type: CdsToastType) => {
    switch (type) {
      case CdsToastType.Success:
        return (
          <CheckCircleIcon
            className="mt-0.5 h-5 w-5 shrink-0"
            style={{ color: twColors[CdsToastTypeColorMap[type]][500] }}
          />
        );
      case CdsToastType.Error:
        return (
          <ExclamationCircleIcon
            className="mt-0.5 h-5 w-5 shrink-0"
            style={{ color: twColors[CdsToastTypeColorMap[type]][500] }}
          />
        );
      case CdsToastType.Warning:
        return (
          <ExclamationIcon
            className="mt-0.5 h-5 w-5 shrink-0"
            style={{ color: twColors[CdsToastTypeColorMap[type]][500] }}
          />
        );
      case CdsToastType.Default:
        return (
          <InformationCircleIcon
            className="mt-0.5 h-5 w-5 shrink-0"
            style={{ color: twColors[CdsToastTypeColorMap[type]][500] }}
          />
        );
    }
  }, []);

  return (
    <div className="flex items-start gap-2 p-1">
      {!showLoader && type && getToastIconByType(type)}

      {showLoader && (
        <div className="mt-1 h-5 w-5 shrink-0">
          <InlineLoader />
        </div>
      )}

      <div className="flex grow flex-col gap-1">
        <span className="flex text-base font-normal text-white">{message}</span>

        {buttonText && type && (
          <span
            className="cursor-pointer text-base font-medium"
            style={{ color: twColors[CdsToastTypeColorMap[type]][400] }}
            onClick={buttonOnClick}
          >
            {buttonText}
          </span>
        )}
      </div>

      {closeButton && (
        <XIcon
          className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer text-slate-300"
          onClick={onClose}
        />
      )}
    </div>
  );
};
